<template>
  <Teleport to="body">
    <Transition name="dlg">
      <!-- Le fond ne ferme RIEN. Un clic à côté, ou un relâchement de souris hors
           du panneau pendant une sélection de texte, faisait perdre la saisie —
           c'est la raison pour laquelle `closeOnOverlay` a été supprimé du kit v1
           en août 2026, et elle vaut toujours. Les seules sorties sont la croix,
           le pied, et Échap quand rien n'a été saisi (cf. `guard`). -->
      <div v-if="modelValue" class="dlg-scrim" role="presentation">
        <div
          ref="panelRef"
          :class="['dlg', `dlg-${size}`, { 'dlg-fit': bodyFit }, panelClass]"
          role="dialog"
          :aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          :data-testid="$attrs['data-testid']"
        >
          <header v-if="$slots.header || title" class="dlg-head">
            <slot name="header" :title-id="titleId">
              <div class="dlg-titles">
                <!-- `title-extra` : ce qui se lit AVEC le titre sans en faire
                     partie (un « i » d'explication). Hors du `h2`, pour ne pas
                     entrer dans le nom accessible de la fenêtre. Absent, le
                     rendu est exactement celui d'avant. -->
                <div v-if="$slots['title-extra']" class="dlg-title-row">
                  <h2 :id="titleId" class="dlg-title">{{ title }}</h2>
                  <slot name="title-extra"></slot>
                </div>
                <h2 v-else :id="titleId" class="dlg-title">{{ title }}</h2>
                <p v-if="subtitle" class="dlg-sub">{{ subtitle }}</p>
              </div>
            </slot>
            <button
              v-if="closable"
              type="button"
              class="dlg-close"
              aria-label="Fermer la fenêtre"
              @click="attemptClose"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
              Fermer
            </button>
          </header>

          <!-- Ce qui doit rester à vue quel que soit le défilement du corps :
               une barre d'onglets, un fil de navigation interne. Absent, ce
               bloc ne rend rien et ne change donc rien aux fenêtres existantes. -->
          <div v-if="$slots.toolbar" class="dlg-toolbar">
            <slot name="toolbar"></slot>
          </div>

          <div :class="['dlg-body', { 'dlg-body-flush': flush }]">
            <slot></slot>
          </div>

          <footer v-if="$slots.footer || $slots['footer-start']" class="dlg-foot">
            <div v-if="$slots['footer-start']" class="dlg-foot-start">
              <slot name="footer-start"></slot>
            </div>
            <div class="dlg-foot-end">
              <slot name="footer"></slot>
            </div>
          </footer>

          <!-- Ce qui se superpose au panneau : la garde anti-perte de saisie. -->
          <slot name="overlay"></slot>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/**
 * COQUILLE DE POP-UP v2.
 *
 * Distincte de `Modal.vue`, l'ancien kit de fenêtres modales.
 *
 * Ce qu'elle apporte par rapport à `Modal.vue` :
 * — un SOUS-TITRE, qui dit sur quoi on agit (« Établissements Vaillant & Fils »)
 *   plutôt que de laisser le titre porter seul l'identification ;
 * — un pied à TROIS emplacements (destructif à gauche, retrait puis action à
 *   droite), au lieu d'un `justify-content: flex-end` que chaque page rhabillait ;
 * — une GARDE de fermeture : `guard` à vrai, Échap et la croix n'ont plus
 *   d'effet direct, ils émettent `close-blocked` et c'est l'appelant qui décide.
 */
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { useFocusTrap } from '../../../composables/ui/useFocusTrap.js'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: null },
  /** Ce sur quoi la fenêtre agit : l'entité, sa référence, son état. */
  subtitle: { type: String, default: null },
  size: {
    type: String,
    default: 'medium',
    validator: (v) => ['small', 'medium', 'large', 'xlarge', 'fullscreen'].includes(v)
  },
  closable: { type: Boolean, default: true },
  /** Retire le rembourrage du corps : la fenêtre fournit sa propre mise en page. */
  flush: { type: Boolean, default: false },
  /**
   * CORPS AJUSTÉ. La fenêtre prend une hauteur DÉFINIE et son corps cesse de
   * défiler : c'est un enfant du corps — une zone de saisie, une liste — qui
   * porte le défilement, là où il a un sens. Tout ce qui l'entoure, dernière
   * case et pied compris, reste visible à toute hauteur d'écran.
   *
   * Opt-in, et c'est la condition pour y toucher : une évolution du kit
   * `ui/dialog/` n'a pas le droit de changer ce que rendent les fenêtres
   * existantes. Sans la prop, pas une règle ne s'applique.
   *
   * Le plafond se règle par `--dlg-fit-max` sur le panneau (880 px par défaut) :
   * au-delà, une fenêtre plus haute n'apporte plus rien à la saisie et éloigne
   * le pied du regard.
   */
  bodyFit: { type: Boolean, default: false },
  /**
   * Vrai quand une saisie non enregistrée est en cours. Échap et la croix
   * cessent alors de fermer et émettent `close-blocked`.
   */
  guard: { type: Boolean, default: false },
  panelClass: { type: String, default: null }
})

const emit = defineEmits(['update:modelValue', 'close', 'close-blocked'])

const panelRef = ref(null)
const isActive = computed(() => props.modelValue)
useFocusTrap(panelRef, isActive)

const titleId = computed(() => `dlg-title-${Math.random().toString(36).slice(2, 11)}`)

const close = () => {
  emit('update:modelValue', false)
  emit('close')
}

const attemptClose = () => {
  if (props.guard) {
    emit('close-blocked')
    return
  }
  close()
}

const onKeydown = (e) => {
  if (e.key === 'Escape' && props.modelValue) attemptClose()
}

const bind = (on) => {
  if (on) {
    document.addEventListener('keydown', onKeydown)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''
  }
}

watch(() => props.modelValue, bind)
// Une fenêtre démontée alors qu'elle est ouverte (changement de route) laissait
// `overflow: hidden` sur le body : la page derrière ne défilait plus.
onBeforeUnmount(() => bind(false))

defineExpose({ close })
</script>

<style scoped>
.dlg-scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-md);
  overflow-y: auto;
}

.dlg {
  position: relative;
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-height: 90vh;
  margin: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* CORPS AJUSTÉ (`body-fit`). Une hauteur DÉFINIE, et non plus un plafond : sans
   elle, le `flex: 1` de la zone élastique n'a aucun référent et la fenêtre
   retombe à sa hauteur naturelle. `min()` et non `calc()` — aucune constante de
   chrome à deviner ici, le panneau vit dans un voile `fixed; inset: 0`, donc
   `vh` est exact. */
.dlg-fit {
  height: min(90vh, var(--dlg-fit-max, 880px));
}

/* Le corps DISTRIBUE au lieu de défiler — tant que les minima de ses champs
   tiennent. En dessous (une fenêtre de portable très basse, un zoom fort), il
   redevient défilant plutôt que d'écraser : `overflow-y: auto` et non `hidden`.
   Sans cette réserve, la zone de saisie se comprimait sous sa propre barre
   d'outils et son contenu disparaissait SANS ascenseur — un défaut pire que
   celui qu'on corrige, parce qu'il ne laisse aucun moyen d'atteindre la suite. */
.dlg-fit .dlg-body {
  overflow-y: auto;
  min-height: 0;
}

.dlg-small { max-width: 400px; }
.dlg-medium { max-width: 600px; }
.dlg-large { max-width: 800px; }
.dlg-xlarge { max-width: 1200px; }
.dlg-fullscreen { max-width: 95vw; width: 95vw; height: 90vh; }

/* 20/24 px et non 32/24 : le titre s'aligne sur la colonne du corps. */
.dlg-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-md);
  padding: var(--spacing-5) var(--spacing-6);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-primary);
}

.dlg-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dlg-title-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  min-width: 0;
}

.dlg-title {
  margin: 0;
  font-size: var(--font-size-xl);
  line-height: var(--line-height-tight);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.dlg-sub {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: 1.4;
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Bouton porté par la fenêtre, et non `Button variant="close"` : celui-ci est
   un texte gris sans bordure, invisible à côté d'un titre de 20 px. Le changer
   globalement aurait déplacé la croix partout où ce variant est utilisé. */
.dlg-close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  min-height: 36px;
  padding: 0 var(--spacing-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--transition-base), border-color var(--transition-base), color var(--transition-base);
}

.dlg-close:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-border-dark);
  color: var(--color-text-primary);
}

.dlg-close:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* BARRE FIXE SOUS L'EN-TÊTE (slot `toolbar`). Elle ne défile pas avec le corps :
   une fenêtre à onglets qui perd ses onglets au premier coup de molette ne dit
   plus où l'on en est. Le rembourrage horizontal est celui du corps moins
   l'écart interne des onglets, pour que le premier onglet s'aligne sur la
   colonne des champs. */
.dlg-toolbar {
  flex: 0 0 auto;
  padding: 0 var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-primary);
}

.dlg-body {
  flex: 1;
  padding: var(--spacing-6);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-5);

  /* LE CORPS QUI DÉBORDE LE DIT. Une ombre courte apparaît du côté où il reste
     du contenu, et s'efface une fois le bout atteint. Sans elle, une phrase
     d'aide sectionnée par le bord du corps se lit comme une phrase TRONQUÉE et
     non comme « la suite est en dessous » : c'est exactement ce que montraient
     les deux fenêtres de questionnaire, coupées au milieu d'une ligne.
     Les deux premières couches sont le fond du panneau, arrimées au CONTENU
     (`local`) : elles glissent avec lui et découvrent les deux ombres restées
     collées au cadre (`scroll`). Aucun écouteur de défilement, donc rien à
     débrancher au démontage. */
  background-color: var(--color-bg-primary);
  background-image:
    linear-gradient(var(--color-bg-primary) 30%, transparent),
    linear-gradient(transparent, var(--color-bg-primary) 70%),
    radial-gradient(farthest-side at 50% 0, rgba(15, 23, 42, 0.18), transparent),
    radial-gradient(farthest-side at 50% 100%, rgba(15, 23, 42, 0.18), transparent);
  background-position: 0 0, 0 100%, 0 0, 0 100%;
  background-size: 100% 20px, 100% 20px, 100% 10px, 100% 10px;
  background-repeat: no-repeat;
  background-attachment: local, local, scroll, scroll;
}

.dlg-body-flush {
  padding: 0;
  overflow: hidden;
  gap: 0;
  min-height: 0;
  /* Un corps sans rembourrage ne défile pas : ses ombres se figeraient aux deux
     bords sans jamais rien signaler. */
  background-image: none;
}

.dlg-foot {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding: var(--spacing-md) var(--spacing-6);
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-primary);
  flex-wrap: wrap;
}

/* Ce qui détruit se tient à distance de ce qui enregistre. */
.dlg-foot-start {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
}

.dlg-foot-end {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-3);
}

@media (max-width: 768px) {
  .dlg-scrim {
    padding: 0;
    align-items: flex-end;
  }

  .dlg {
    max-width: 100%;
    max-height: 92vh;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    margin: 0;
  }

  /* En feuille, la hauteur ajustée suit le plafond de la feuille (92vh) et
     laisse tomber le sien : sur un téléphone, 880 px, c'est plus que l'écran. */
  .dlg-fit {
    height: 92vh;
  }

  .dlg-fullscreen { width: 100%; height: 92vh; }

  /* Poignée de feuille : la fenêtre remonte du bas, elle doit le dire. */
  .dlg::before {
    content: '';
    position: absolute;
    top: var(--spacing-2);
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: var(--color-border);
    z-index: 11;
  }

  .dlg-head {
    padding: var(--spacing-5) var(--spacing-md) var(--spacing-md);
  }

  .dlg-title { font-size: var(--font-size-lg); }

  .dlg-body { padding: var(--spacing-md); }

  /* Le pied s'empile à l'envers : l'action au-dessus, le retrait en dessous.
     Le pouce tombe sur ce pour quoi la fenêtre a été ouverte, pas sur « Annuler ». */
  .dlg-foot {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: var(--spacing-2);
    padding: var(--spacing-3) var(--spacing-md);
    position: sticky;
    bottom: 0;
  }

  .dlg-foot-start,
  .dlg-foot-end {
    margin-left: 0;
    flex-direction: column-reverse;
    align-items: stretch;
    gap: var(--spacing-2);
  }

  .dlg-foot :deep(.btn) {
    width: 100%;
    justify-content: center;
    min-height: 48px;
  }
}

.dlg-enter-active,
.dlg-leave-active { transition: opacity 0.25s ease; }

.dlg-enter-active .dlg,
.dlg-leave-active .dlg { transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease; }

.dlg-enter-from { opacity: 0; }
.dlg-enter-from .dlg { transform: translateY(30px) scale(0.95); opacity: 0; }
.dlg-leave-to { opacity: 0; }
.dlg-leave-to .dlg { transform: translateY(20px) scale(0.98); opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .dlg-enter-active,
  .dlg-leave-active,
  .dlg-enter-active .dlg,
  .dlg-leave-active .dlg { transition: none; }
}
</style>
